/**
 * Notification List Item
 *
 * One notification row: type icon, title, body, date and location badge,
 * with an accent bar + bold title while unread. Shared by the bell dropdown
 * (NotificationList) and the history modal (NotificationHistoryModal).
 */

import React from 'react';
import type { NotificationItem } from '@/types/notification.types';
import { getNotificationConfig } from '../config/notificationTypes';
import { getLocationShortName } from '../store/locationsStore';

interface NotificationListItemProps {
  notification: NotificationItem;
  isRead: boolean;
  onClick: (notification: NotificationItem) => void;
}

export const NotificationListItem: React.FC<NotificationListItemProps> = ({ notification, isRead, onClick }) => {
  // Format date: MM/DD/YYYY, HH:MM AM/PM
  const dateStr = new Date(notification.timestamp).toLocaleString([], {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit'
  });

  return (
    <div
      className={`p-4 border-b border-line hover:bg-surface-muted transition-colors cursor-pointer relative group ${
        !isRead ? 'bg-surface-muted' : 'bg-surface'
      }`}
      onClick={() => onClick(notification)}
    >
      {/* Unread Indicator */}
      {!isRead && (
        <div className={`absolute left-0 top-0 bottom-0 w-1 bg-accent-strong`}></div>
      )}

      <div className="flex gap-3">
        {/* Icon based on type */}
        <div className="flex-shrink-0 mt-1">
          <NotificationIcon type={notification.type} />
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm text-ink ${!isRead ? 'font-bold' : 'font-medium'}`}>
            {notification.title}
          </p>
          <p className="text-sm text-ink-body mt-1">
            {notification.body}
          </p>
          <div className="flex items-center gap-2 mt-1.5">
            <p className="text-xs text-ink-muted font-medium">
              {dateStr}
            </p>
            {notification.source !== 'personal' && (
            <span className="text-xs bg-surface-inset text-ink-body px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1">
              <i className="fa-solid fa-location-dot text-[9px]"></i>
              {getLocationShortName(notification.locationId)}
            </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * List notification icon using the centralized notification type registry
 */
const NotificationIcon = ({ type }: { type: string }) => {
  const config = getNotificationConfig(type);
  return (
    <div className={`w-8 h-8 rounded-full ${config.listIconBg} flex items-center justify-center ${config.listIconText}`}>
      <i className={`fa-solid ${config.icon} text-sm`}></i>
    </div>
  );
};
